import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatPrice, formatDate } from "../lib/api";
import { formatAutoReject } from "../lib/formatAutoReject";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
    Shield, ArrowLeft, MessageSquare, Gavel, Handshake, Repeat, Heart,
    LogIn, Eye, FileText, CheckCircle2, CreditCard, AlertCircle, Activity,
} from "lucide-react";

const EVENT_META = {
    listing_created: { icon: FileText, label: "Inserat erstellt", color: "text-gray-700" },
    listing_updated: { icon: FileText, label: "Inserat bearbeitet", color: "text-gray-600" },
    listing_deleted: { icon: FileText, label: "Inserat gelöscht", color: "text-red-600" },
    bid_placed: { icon: Gavel, label: "Gebot abgegeben", color: "text-orange-700" },
    buy_now_bid: { icon: Gavel, label: "Sofort-Kauf-Gebot", color: "text-orange-700" },
    buy_now: { icon: Gavel, label: "Sofort gekauft", color: "text-orange-800" },
    offer_created: { icon: Handshake, label: "Preisvorschlag", color: "text-amber-700" },
    offer_countered: { icon: Handshake, label: "Gegenvorschlag", color: "text-blue-700" },
    offer_accepted: { icon: CheckCircle2, label: "Vorschlag angenommen", color: "text-green-700" },
    offer_rejected: { icon: AlertCircle, label: "Vorschlag abgelehnt", color: "text-red-700" },
    offer_auto_rejected: { icon: AlertCircle, label: "Automatisch abgelehnt", color: "text-red-600" },
    offer_withdrawn: { icon: AlertCircle, label: "Vorschlag zurückgezogen", color: "text-gray-600" },
    tradein_created: { icon: Repeat, label: "Inzahlungnahme angefragt", color: "text-blue-700" },
    tradein_countered: { icon: Repeat, label: "Inzahlung-Gegenvorschlag", color: "text-blue-700" },
    tradein_accepted: { icon: CheckCircle2, label: "Inzahlung angenommen", color: "text-green-700" },
    tradein_rejected: { icon: AlertCircle, label: "Inzahlung abgelehnt", color: "text-red-700" },
    tradein_withdrawn: { icon: AlertCircle, label: "Inzahlung zurückgezogen", color: "text-gray-600" },
    chat_message: { icon: MessageSquare, label: "Chat-Nachricht", color: "text-[#2563EB]" },
    favorite_added: { icon: Heart, label: "Favorisiert", color: "text-pink-600" },
    favorite_removed: { icon: Heart, label: "Favorit entfernt", color: "text-gray-500" },
    payment_completed: { icon: CreditCard, label: "Zahlung abgeschlossen", color: "text-green-700" },
    login_success: { icon: LogIn, label: "Anmeldung", color: "text-gray-500" },
    login_failed: { icon: LogIn, label: "Fehlgeschlagene Anmeldung", color: "text-red-600" },
    user_registered: { icon: LogIn, label: "Registrierung", color: "text-gray-500" },
};

function PayloadPreview({ payload }) {
    if (!payload || Object.keys(payload).length === 0) return null;
    return (
        <details className="mt-1">
            <summary className="text-[10px] text-gray-400 cursor-pointer uppercase tracking-wider">Payload</summary>
            <pre className="mt-1 text-[11px] bg-gray-50 border border-gray-200 rounded-sm p-2 overflow-x-auto font-mono-num text-gray-700">{JSON.stringify(payload, null, 2)}</pre>
        </details>
    );
}

function EventRow({ event }) {
    const meta = EVENT_META[event.event_type] || { icon: Activity, label: event.event_type, color: "text-gray-600" };
    const Icon = meta.icon;
    const content = event.payload?.content;
    return (
        <div className="flex gap-3 py-3 px-4 hover:bg-gray-50 border-b border-gray-100" data-testid={`event-${event.event_type}`}>
            <div className={`mt-0.5 ${meta.color}`}>
                <Icon className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-sm">
                        <span className={`font-bold ${meta.color}`}>{meta.label}</span>
                        {event.actor_label && <span className="text-gray-600"> · {event.actor_label}</span>}
                    </div>
                    <span className="text-[10px] text-gray-400 font-mono-num">{formatDate(event.created_at)}</span>
                </div>
                {content && (
                    <div className="mt-1 bg-blue-50 border-l-2 border-[#2563EB] px-2 py-1 text-sm italic text-gray-700">„{content}"</div>
                )}
                {!content && event.payload?.amount && (
                    <div className="mt-1 text-xs text-gray-600 font-mono-num">
                        Betrag: <strong>{formatPrice(event.payload.amount)}</strong>
                        {(event.payload.auto_reject_code || event.payload.auto_reject_reason) && (
                            <span className="ml-2 text-red-600">· {formatAutoReject(event.payload.auto_reject_code, event.payload.auto_reject_params, event.payload.auto_reject_reason)}</span>
                        )}
                    </div>
                )}
                <div className="flex gap-3 text-[10px] text-gray-400 mt-1">
                    {event.ip && <span>IP: <span className="font-mono-num">{event.ip}</span></span>}
                    {event.user_agent && <span className="truncate max-w-[400px]">UA: {event.user_agent}</span>}
                </div>
                <PayloadPreview payload={event.payload} />
            </div>
        </div>
    );
}

function ChatThread({ thread }) {
    const [a, b] = thread.participants || [];
    const renderParticipant = (p) => {
        if (!p) return <span className="text-gray-400 italic">(unbekannt)</span>;
        if (p.deleted) return <span className="text-gray-400 italic" title="Account gelöscht">{p.name || "(gelöschter Nutzer)"}</span>;
        return <strong>{p.company_name || p.name || "?"}</strong>;
    };
    return (
        <div className="border border-gray-200 rounded-sm mb-3 bg-white" data-testid="chat-thread">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 text-sm">
                    <MessageSquare className="w-4 h-4 text-[#2563EB]" />
                    {renderParticipant(a)}
                    <span className="text-gray-400">↔</span>
                    {renderParticipant(b)}
                </div>
                <Badge className="bg-[#2563EB] hover:bg-[#2563EB] text-white text-[10px] uppercase tracking-wider rounded-sm">
                    {thread.message_count} Nachrichten · {formatDate(thread.first_at)} – {formatDate(thread.last_at)}
                </Badge>
            </div>
            <ScrollArea className="max-h-[300px]">
                <div className="p-3 space-y-2">
                    {thread.messages.map((m) => (
                        <div key={m.id} className="text-sm flex gap-3" data-testid="chat-message">
                            <span className="text-[10px] text-gray-400 font-mono-num shrink-0 w-20">{new Date(m.created_at).toLocaleTimeString("de-DE")}</span>
                            <div className="flex-1 min-w-0">
                                <div className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">{m.from_name || m.from_user_id}</div>
                                <div className="text-gray-800">{m.content}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </ScrollArea>
        </div>
    );
}

export default function AdminListingLogPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        if (user && user !== false && user.role !== "admin") {
            navigate("/");
            return;
        }
    }, [user, navigate]);

    useEffect(() => {
        api.get(`/admin/listings/${id}/log`)
            .then((r) => setData(r.data))
            .catch((e) => setError(e.response?.data?.detail || e.message));
    }, [id]);

    if (error) return <div className="max-w-5xl mx-auto p-8"><div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-sm" data-testid="admin-log-error">{error}</div></div>;
    if (!data) return <div className="max-w-5xl mx-auto p-8 text-gray-500" data-testid="admin-log-loading">Lädt Protokoll…</div>;

    const lst = data.listing;
    return (
        <div className="bg-gray-50 min-h-screen pb-12" data-testid="admin-listing-log">
            <div className="bg-[#111827] text-white">
                <div className="max-w-5xl mx-auto px-6 py-6">
                    <Link to="/" className="text-xs uppercase tracking-wider text-gray-400 hover:text-white inline-flex items-center gap-1 mb-3">
                        <ArrowLeft className="w-3 h-3" /> Zurück
                    </Link>
                    <div className="flex items-center gap-2 mb-2">
                        <Shield className="w-5 h-5 text-[#FFCC00]" />
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[#FFCC00]">Admin · Audit-Protokoll</span>
                    </div>
                    <h1 className="font-display font-black text-3xl">{lst.title}</h1>
                    <div className="text-sm text-gray-300 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                        <span>ID: <span className="font-mono-num">{lst.id}</span></span>
                        <span>Kategorie: {lst.category}</span>
                        <span>Typ: {lst.sale_type}</span>
                        <span>Preis: <span className="font-mono-num">{formatPrice(lst.price)}</span></span>
                        <span>Besitzer: {lst.owner?.company_name || lst.owner?.name}</span>
                        {lst.sold && <Badge className="bg-red-600 text-white">VERKAUFT</Badge>}
                        {lst.paid && <Badge className="bg-green-600 text-white">BEZAHLT</Badge>}
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-6 mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-white border border-gray-200 rounded-sm p-4">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">Events</div>
                    <div className="font-display font-black text-3xl mt-1 font-mono-num" data-testid="stat-events">{data.stats.total_events}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-sm p-4">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">Chats</div>
                    <div className="font-display font-black text-3xl mt-1 font-mono-num" data-testid="stat-chats">{data.stats.chat_threads}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-sm p-4">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">Chat-Nachrichten</div>
                    <div className="font-display font-black text-3xl mt-1 font-mono-num" data-testid="stat-chat-messages">{data.stats.chat_messages}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-sm p-4">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">Aufrufe</div>
                    <div className="font-display font-black text-3xl mt-1 font-mono-num" data-testid="stat-views"><Eye className="inline w-4 h-4 mr-1 text-gray-400" />{data.stats.views_total}</div>
                </div>
            </div>

            {data.chats.length > 0 && (
                <div className="max-w-5xl mx-auto px-6 mt-8">
                    <div className="flex items-center gap-2 mb-3">
                        <MessageSquare className="w-5 h-5 text-[#2563EB]" />
                        <h2 className="font-display font-bold text-xl">Chat-Verlauf ({data.chats.length} Thread{data.chats.length === 1 ? "" : "s"})</h2>
                    </div>
                    {data.chats.map((t) => <ChatThread key={t.thread_id} thread={t} />)}
                </div>
            )}

            <div className="max-w-5xl mx-auto px-6 mt-8">
                <div className="flex items-center gap-2 mb-3">
                    <Activity className="w-5 h-5 text-[#FFCC00]" />
                    <h2 className="font-display font-bold text-xl">Chronologisches Protokoll</h2>
                </div>
                <div className="bg-white border border-gray-200 rounded-sm" data-testid="events-list">
                    {data.events.length === 0 ? (
                        <div className="p-8 text-center text-gray-400 text-sm">Noch keine Events erfasst.</div>
                    ) : data.events.map((e) => <EventRow key={e.id} event={e} />)}
                </div>
            </div>

            {data.views_by_day.length > 0 && (
                <div className="max-w-5xl mx-auto px-6 mt-8">
                    <div className="flex items-center gap-2 mb-3">
                        <Eye className="w-5 h-5 text-gray-500" />
                        <h2 className="font-display font-bold text-xl">Aufrufe nach Tag</h2>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
                        {data.views_by_day.map((v) => (
                            <div key={v.day} className="flex justify-between items-center px-4 py-2 border-b border-gray-100 last:border-0 text-sm">
                                <span className="font-mono-num text-gray-600">{v.day}</span>
                                <span className="font-mono-num font-bold">{v.count}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="max-w-5xl mx-auto px-6 mt-10 text-center text-[10px] text-gray-400">
                <Shield className="inline w-3 h-3 mr-1" /> Vertraulich · nur für Plattform-Administratoren
            </div>
        </div>
    );
}
